// @flow

import type {Product} from "./Product";

// Manufacturer (commercial) guarantee behind the EU GARAN label (EmpCo, #56424).
export type ManufacturerWarranty = {
    modelIdentifier: string,
    durationYears: number,
};

// The EU GARAN label only exists for guarantees that exceed the two-year legal guarantee.
export const LEGAL_GUARANTEE_YEARS = 2;

export const exceedsLegalGuarantee = (warranty: ?ManufacturerWarranty): boolean =>
    !!warranty && Number(warranty.durationYears) > LEGAL_GUARANTEE_YEARS;

// Shown whenever a guarantee above the legal two years is known for an EU shop. Brand and model
// identifier are printed on the label when present; a dealer's own product without a brand still
// gets its label, with that field left empty.
export const hasWarrantyLabel = (product: ?Product, euShop: ?boolean): boolean =>
    !!euShop
    && !!product
    && exceedsLegalGuarantee(product.manufacturerWarranty);

// The label is language-neutral; the EU guidelines prescribe a comma before the half year ("2,5").
export const formatWarrantyYears = (durationYears: number | string): string => {
    const years = Number(durationYears);
    if (Number.isNaN(years)) {
        return "";
    }
    return Number.isInteger(years) ? String(years) : years.toFixed(1).replace(".", ",");
};

// For flowing translated sentences the half year must follow the reader's locale (e.g. "2.5" in en_gb),
// unlike the language-neutral label graphic above which always carries the EU comma.
export const formatWarrantyYearsText = (durationYears: number | string, locale: string): string => {
    const years = Number(durationYears);
    if (Number.isNaN(years)) {
        return "";
    }
    const bcp47 = (locale || "en").replace(/_/g, "-");
    return new Intl.NumberFormat(bcp47, {maximumFractionDigits: 1}).format(years);
};
